import {useRef, useEffect, useCallback, useState, SetStateAction} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import {Popper, PopperTrigger} from '@baidu/luffy-comp-popper';
import {reportQuickAuthorizePopper, reportQuickAuthorizeBtn} from '@/buried';
import sdk from '@/sdk';
import Text, {TextType} from '@/components/Text';
import Button, {ButtonType, ButtonSize} from '@/components/Button';
import {MemberType, EDITOR_MODE, DocRole} from '@/Constant';
import {useAuthorizeRead} from '@/hooks/authorize';
import styles from './index.module.scss';
import Icon from '@/components/Icon';
import {ArrowDownSvg} from '@/icons';
import arrowDownSvg from '@/assets/svg/arrowDownSvg.svg?tsx';
import {curDocCanInviteEditor} from '@/store/util';

class Cache {
    data: any;
    constructor() {
        this.data = {};
    }

    add(id: string) {
        this.data[id] = true;
    }
    has(flag: string) {
        return !!this.data[flag];
    }
    reset() {
        this.data = {};
    }
}

const cache = new Cache();

interface IParams {
    repository: Repository;
    docGuid: string;
    editorMode: EDITOR_MODE;
    data: {
        target: HTMLElement;
        from: string;
    };
}
interface IProps extends IParams {
    onClose: () => void;
}

const duration = 5000;

function AuthorizePopper(props: IProps) {
    const timerRef = useRef<any>();
    const {repository, docGuid, data, onClose} = props;

    const close = useCallback(() => {
        onClose();
    }, [onClose]);

    const authorizeRead = useAuthorizeRead();
    const iscurDocCanInviteEditor = curDocCanInviteEditor(); // 当前用户能否在当前文档邀请编辑者

    const permissionOptions = [
        {
            label: '可阅读',
            value: 0,
            role: DocRole.Reader,
        },
        {
            label: '可编辑',
            value: 1,
            role: DocRole.Member,
            disabled: !iscurDocCanInviteEditor,
        },
    ];
    const [selectedOption, setSelectedOption] = useState<{
        label: string;
        value: number;
        role: typeof DocRole.Member | typeof DocRole.Reader; // 指定类型为 DocRole 的字段
    }>(permissionOptions[0]);
    async function onConfirm() {
        const {mentionText, mentionId} = data.target.dataset;
        const result: boolean = await authorizeRead(repository, docGuid, [
            {
                nickname: mentionText,
                memberType: MemberType.User,
                memberGuid: mentionId,
            },
        ], selectedOption.role);

        if (result) {
            setSelectedOption(selectedOption);
            close();
        }

        reportQuickAuthorizeBtn({
            accuser_id: sdk.as.user?.userGuid,
            page_id: docGuid,
            kb_id: repository.repositoryGuid,
            // 来源: 0 正文，1: 评论
            source: data.from === 'comment' ? 1 : 0,
            suser_id: mentionId,
            type: 0,
        });
    }

    function onMouseEnter() {
        clearTimeout(timerRef.current);
    }

    const timeoutClose = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            close();
        }, duration);
    }, [close]);

    useEffect(() => {
        timeoutClose();

        const {mentionId} = data.target.dataset;
        reportQuickAuthorizePopper({
            accuser_id: sdk.as.user?.userGuid,
            page_id: docGuid,
            kb_id: repository.repositoryGuid,
            // 来源: 0 正文，1: 评论
            source: data.from === 'comment' ? 1 : 0,
            suser_id: mentionId,
            type: 0,
        });

        return () => {
            clearTimeout(timerRef.current);
        };
    }, [
        data.target,
        data.from,
        timeoutClose,
        docGuid,
        repository.userGuid,
        repository.repositoryGuid,
    ]);

    // target元素被移除后，关闭弹窗
    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (!data.target.parentElement) {
                close();
            }
        });

        observer.observe(data.target.parentElement as HTMLElement, {childList: true});

        return () => {
            observer.disconnect();
        };
    }, [data.target, close]);

    const onChange = useCallback(
        (visible: boolean) => {
            if (!visible) {
                close();
            }
        },
        [close]
    );

    const name = data.target?.dataset.mentionText;
    const [dropdownVisible, setDropdownVisible] = useState(false);

    const toggleDropdown = () => {
        setDropdownVisible(!dropdownVisible);
    };

    const handleOptionSelect = (option: {label: string, value: number, role: string, disabled?: boolean}) => {
        if (!option.disabled) {
            setSelectedOption(option);
            setDropdownVisible(false);
        }
    };

    return (
        <PopperTrigger defaultVisible target={data.target} onChange={onChange}>
            <Popper
                className={styles.popperWrap}
                destroyOnHide={false}
                placement={data.from === 'comment' ? 'top' : 'bottom'}
                onMouseEnter={onMouseEnter}
                onMouseLeave={timeoutClose}
                preventOverflowBoundary={'viewport'}
            >
                <div className={classNames(styles.wrap)}>
                    <div className={styles.content}>
                        <b>{name}&nbsp;</b>
                        暂无页面访问权限，是否授权&nbsp;
                        <div className={styles.dropdown}>
                            <button onClick={toggleDropdown} className={styles.menuBtn}>
                                {selectedOption.label}
                                <Icon
                                    svg={ArrowDownSvg}
                                    className={classNames(styles.iconArrowDown, dropdownVisible && styles.expand)}
                                />
                            </button>
                            {dropdownVisible && (
                                <ul className={styles.dropdownMenu}>
                                    {permissionOptions.map((option) => (
                                        <li
                                            key={option.value}
                                            onClick={() => handleOptionSelect(option)}
                                            className={classNames(
                                                styles.dropdownItem,
                                                selectedOption.value === option.value && styles.selected,
                                                option?.disabled && styles.disabled
                                            )}
                                        >
                                            <span>{option.label}</span>
                                            {selectedOption.value === option.value && (
                                                <Icon svg={arrowDownSvg} className={styles.checkIcon} />
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        &nbsp;权限？
                    </div>
                    <div className={classNames(styles.footer)}>
                        <Button
                            border
                            className={styles.closeBtn}
                            type={ButtonType.Plain}
                            shape="capsule"
                            size={ButtonSize.MD}
                            onClick={close}
                        >
                            取消
                        </Button>
                        <Button
                            noMinWidth
                            className={styles.confirmBtn}
                            type={ButtonType.Important}
                            shape="capsule"
                            size={ButtonSize.MD}
                            onClick={onConfirm}
                        >
                            授权
                        </Button>
                    </div>
                </div>
            </Popper>
        </PopperTrigger>
    );
}

let container: any = null;
// eslint-disable-next-line @typescript-eslint/no-empty-function
export let removeAuthorizePopper: () => void = () => {};

export function authorizePopper(options: IParams) {
    const {data, docGuid} = options;
    const {mentionId} = data.target.dataset;
    const flag = `${docGuid}/${mentionId}`;
    if (cache.has(flag)) {
        return Promise.resolve();
    } else {
        cache.add(flag);

        return new Promise<string>(resolve => {
            function onClose() {
                resolve('closed');
                if (container) {
                    ReactDOM.unmountComponentAtNode(container);
                    document.body.removeChild(container);
                    container = null;
                }
            }

            removeAuthorizePopper();
            removeAuthorizePopper = onClose;

            container = document.createElement('div');
            document.body.appendChild(container);
            ReactDOM.render(<AuthorizePopper {...options} onClose={onClose} />, container);
        });
    }
}
